// DashboardLayout.jsx — navy sidebar + glassmorphism top bar layout
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Avatar, Menu, MenuItem, IconButton, Tooltip, Divider, Chip,
} from "@mui/material";
import {
  LayoutDashboard, Users, Building2, Map, FileSpreadsheet, ClipboardList,
  LineChart, Settings, LogOut, Bell, Menu as MenuIcon, ShieldAlert, ChevronLeft,
  Server, Upload, Layers, Lightbulb, Globe, Printer,
} from "lucide-react";
import { useAuth } from "../components/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Executive Overview", icon: LayoutDashboard, group: "PORTFOLIO" },
  { to: "/borrowers", label: "Borrowers", icon: Users, group: "PORTFOLIO" },
  { to: "/collaterals", label: "Collateral Registry", icon: Building2, group: "PORTFOLIO" },
  { to: "/vulnerability", label: "Vulnerability Tiers", icon: Layers, group: "PORTFOLIO" },
  { to: "/hazard-map", label: "Hazard Map", icon: Map, group: "ANALYSIS" },
  { to: "/scenarios", label: "Scenario Analysis", icon: LineChart, group: "ANALYSIS" },
  { to: "/operations", label: "Bank Own Operations", icon: Server, group: "ANALYSIS" },
  { to: "/mitigation", label: "Mitigation & Plans", icon: Lightbulb, group: "ACTION" },
  { to: "/origination", label: "Origination Console", icon: ClipboardList, group: "ORIGINATION" },
  { to: "/templates", label: "Origination Templates", icon: FileSpreadsheet, group: "ORIGINATION" },
  { to: "/bulk-upload", label: "Bulk Upload", icon: Upload, group: "DATA" },
  { to: "/entities", label: "Entity & Rollout", icon: Globe, group: "DATA" },
  { to: "/board-reports", label: "Board / Committee Packs", icon: Printer, group: "GOVERNANCE" },
  { to: "/reports", label: "Reports & Exports", icon: ShieldAlert, group: "GOVERNANCE" },
];

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED = 64;
const HEADER_HEIGHT = 60;

export default function DashboardLayout({ children, title, subtitle, headerExtra }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const w = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", background: "#F7F7F6" }}>
      {/* SIDEBAR */}
      <Box className="cb-sidebar"
        sx={{
          width: w, position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 30,
          transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Brand */}
        <Box sx={{
          height: HEADER_HEIGHT, display: "flex", alignItems: "center",
          px: collapsed ? 1.5 : 2, borderBottom: "1px solid #2C3E50", gap: 1.4,
        }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1,
            background: "linear-gradient(135deg, #2E5A35 0%, #A3C2A9 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#1E2A38", fontWeight: 700, fontFamily: "Playfair Display, serif",
            fontSize: 18, flexShrink: 0,
          }} data-testid="brand-logo">⏚</Box>
          {!collapsed && (
            <Box>
              <Box sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600, fontSize: 15, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                ClimateLedger
              </Box>
              <Box sx={{ fontSize: 9.5, letterSpacing: "0.16em", color: "#8FA0B5", textTransform: "uppercase", fontFamily: "IBM Plex Mono, monospace" }}>
                Banking · Physical Risk
              </Box>
            </Box>
          )}
        </Box>

        {/* Nav */}
        <Box sx={{ flex: 1, overflowY: "auto", py: 1.5 }}>
          {(() => {
            const groups = {};
            NAV.forEach((n) => { (groups[n.group] = groups[n.group] || []).push(n); });
            return Object.entries(groups).map(([grp, items]) => (
              <Box key={grp} sx={{ mb: 1.6 }}>
                {!collapsed && (
                  <Box sx={{
                    px: 2, py: 0.6, fontSize: 9.5, letterSpacing: "0.14em",
                    color: "#5A6B82", textTransform: "uppercase", fontFamily: "IBM Plex Mono, monospace", fontWeight: 600,
                  }}>{grp}</Box>
                )}
                {items.map((n) => {
                  const Icon = n.icon;
                  const active = loc.pathname === n.to || (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
                  return (
                    <Tooltip key={n.to} title={collapsed ? n.label : ""} placement="right">
                      <Box
                        component={Link}
                        to={n.to}
                        data-testid={`nav-${n.to.replace("/", "")}`}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.4,
                          mx: 1, my: 0.2, px: 1.3, py: 1, borderRadius: 1, cursor: "pointer",
                          textDecoration: "none",
                          color: active ? "#FFFFFF" : "#C8D1DC",
                          backgroundColor: active ? "rgba(46,90,53,0.35)" : "transparent",
                          borderLeft: active ? "2px solid #A3C2A9" : "2px solid transparent",
                          transition: "all 0.16s ease",
                          "&:hover": { background: active ? "rgba(46,90,53,0.45)" : "rgba(200,209,220,0.06)", color: "#FFFFFF" },
                          justifyContent: collapsed ? "center" : "flex-start",
                        }}
                      >
                        <Icon size={16} strokeWidth={2} />
                        {!collapsed && <Box sx={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>{n.label}</Box>}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ));
          })()}
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: "1px solid #2C3E50", px: collapsed ? 1.5 : 2, py: 1.3 }}>
          <IconButton
            onClick={() => setCollapsed((v) => !v)}
            sx={{ color: "#C8D1DC", borderRadius: 1, "&:hover": { background: "rgba(200,209,220,0.08)" } }}
            data-testid="sidebar-collapse-btn"
          >
            <ChevronLeft size={16} style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </IconButton>
        </Box>
      </Box>

      {/* MAIN */}
      <Box sx={{ flex: 1, marginLeft: `${w}px`, transition: "margin-left 0.22s", minWidth: 0 }}>
        {/* TOP BAR */}
        <Box sx={{
          position: "sticky", top: 0, zIndex: 20, height: HEADER_HEIGHT,
          backdropFilter: "blur(10px)", background: "rgba(255,255,255,0.85)",
          borderBottom: "1px solid #E2E8F0",
          display: "flex", alignItems: "center", px: 3, gap: 2,
        }}>
          <IconButton sx={{ display: { md: "none" } }} onClick={() => setCollapsed((v) => !v)}>
            <MenuIcon size={18} />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.4 }}>
              <Box className="cb-display" sx={{ fontSize: 19, fontWeight: 600, color: "#111827", letterSpacing: "-0.02em" }}>
                {title || "Climate Risk"}
              </Box>
              {subtitle && (
                <Box sx={{ fontSize: 12, color: "#6B7280", fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.04em" }}>
                  {subtitle}
                </Box>
              )}
            </Box>
          </Box>

          {headerExtra}

          <Tooltip title="Alerts">
            <IconButton size="small" sx={{ color: "#4B5563" }} data-testid="header-alerts-btn">
              <Bell size={17} />
            </IconButton>
          </Tooltip>

          <Tooltip title={user?.email || ""}>
            <Box
              data-testid="header-account-btn"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex", alignItems: "center", gap: 1.1, cursor: "pointer",
                pr: 1, pl: 0.8, py: 0.6, borderRadius: 1,
                "&:hover": { background: "#F0EFEA" },
              }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: "#1E2A38", fontSize: 12, fontWeight: 700 }}>
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left", maxWidth: 200 }}>
                <Box sx={{ fontSize: 12, fontWeight: 600, color: "#111827", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user?.name || user?.email}
                </Box>
                <Box sx={{ fontSize: 10, color: "#6B7280", fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  {user?.role?.replace("_", " ") || ""}
                </Box>
              </Box>
            </Box>
          </Tooltip>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 200, border: "1px solid #E2E8F0" } } }}>
            <Box sx={{ px: 2, pt: 1, pb: 1.4 }}>
              <Box sx={{ fontSize: 12.5, fontWeight: 600 }}>{user?.name}</Box>
              <Box sx={{ fontSize: 11, color: "#6B7280" }}>{user?.email}</Box>
              <Chip size="small" label={user?.role?.replace("_", " ")} sx={{ mt: 0.6, fontSize: 9.5, textTransform: "uppercase" }} />
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); navigate("/templates"); }}>
              <Settings size={14} style={{ marginRight: 8 }} /> Templates
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); logout(); }} data-testid="logout-menu-item">
              <LogOut size={14} style={{ marginRight: 8 }} /> Sign out
            </MenuItem>
          </Menu>
        </Box>

        {/* PAGE CONTENT */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>

        {/* FOOTER */}
        <Box sx={{
          mt: 4, py: 2.4, textAlign: "center", borderTop: "1px solid #E2E8F0",
          color: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.08em",
        }}>
          ClimateLedger Banking · Physical Risk Intelligence · v1.0
        </Box>
      </Box>
    </Box>
  );
}
